import type { EmployeeWithBrand } from "../digital-cards/card-digital.interface";
import type { Image } from "../media/media.interface";
import { CANVAS_DEFAULT_FONT_FAMILY } from "../shared/font-presets";
import type {
  MeetBackground,
  MeetBackgroundCorner,
  MeetBackgroundEditorFormValues,
  MeetBackgroundEditorSettings,
  MeetBackgroundEmployeeRender,
  MeetBackgroundLayoutVariant,
  MeetBackgroundPreviewLayerParams,
  MeetBackgroundSettings,
  MeetCornerField,
  MeetLayoutConfig,
  MeetPreviewLayers,
  MeetQrPlateMetrics,
  MeetSliderKind,
} from "./meet-background.interface";

export const MEET_LAYOUT: MeetLayoutConfig = {
  frame: { width: 1280, height: 720 },
  scale: { min: 0.5, max: 1, default: 0.7 },
  logoScale: { min: 0.5, max: 2, default: 0.7 },
  qr: {
    frameRatioAtFull: 0.1,
    legacyStoredMax: 0.2,
    plate: {
      padding: 32,
      captionGap: 18,
      surfacePadding: 12,
      radius: 32,
      textMaxWidth: 252,
      maxWidth: 272,
      codeBoost: 1.38,
      fonts: { caption: 30, name: 28, job: 20 },
    },
  },
  logo: {
    frameRatioAtFull: 0.12,
    legacyStoredMax: 0.25,
    plate: { padding: 4, radius: 6, minInnerHeight: 16 },
  },
};

export const MEET_BACKGROUND_CORNERS: MeetBackgroundCorner[] = [
  "bottom-right",
  "bottom-left",
  "top-right",
  "top-left",
];

export function meetSlider(value: unknown, kind: MeetSliderKind): number {
  const limits = kind === "qr" ? MEET_LAYOUT.scale : MEET_LAYOUT.logoScale;
  const target = kind === "qr" ? MEET_LAYOUT.qr : MEET_LAYOUT.logo;
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return limits.default;
  }
  const slider =
    value <= target.legacyStoredMax ? value / target.frameRatioAtFull : value;
  return Math.min(limits.max, Math.max(limits.min, slider));
}

function scaledPx(base: number, slider: number): number {
  if (base === 0) {
    return 0;
  }
  return Math.max(1, Math.round(base * slider));
}

export function meetQrPlateMetrics(
  sizeRatio: number,
  layoutVariant: MeetBackgroundLayoutVariant = "corner",
  layout: MeetLayoutConfig = MEET_LAYOUT,
): MeetQrPlateMetrics {
  const slider = meetSlider(sizeRatio, "qr");
  const { plate } = layout.qr;
  const frameRatio = slider * layout.qr.frameRatioAtFull;
  const padding = scaledPx(plate.padding, slider);

  if (layoutVariant === "full-overlay") {
    return {
      sizeRatio: slider,
      qrSizePx: Math.round(layout.frame.height * 0.45 * slider),
      platePaddingX: scaledPx(48, slider),
      platePaddingY: scaledPx(48, slider),
      qrToTextGap: 0,
      plateGap: scaledPx(plate.captionGap, slider),
      identityGap: scaledPx(8, slider),
      surfacePadding: scaledPx(plate.surfacePadding, slider),
      plateRadius: 0,
      textLayoutMaxWidth: Math.round(layout.frame.width * 0.7),
      fontCaption: scaledPx(plate.fonts.caption, slider),
      fontName: scaledPx(plate.fonts.name, slider),
      fontJob: scaledPx(plate.fonts.job, slider),
      plateMaxWidthPx: layout.frame.width,
    };
  }

  if (layoutVariant === "bottom-bar") {
    return {
      sizeRatio: slider,
      qrSizePx: Math.round(layout.frame.width * frameRatio * plate.codeBoost),
      platePaddingX: padding,
      platePaddingY: scaledPx(20, slider),
      qrToTextGap: scaledPx(20, slider),
      plateGap: scaledPx(8, slider),
      identityGap: scaledPx(4, slider),
      surfacePadding: scaledPx(plate.surfacePadding, slider),
      plateRadius: scaledPx(20, slider),
      textLayoutMaxWidth: scaledPx(360, slider),
      fontCaption: scaledPx(plate.fonts.caption, slider),
      fontName: scaledPx(plate.fonts.name, slider),
      fontJob: scaledPx(plate.fonts.job, slider),
      plateMaxWidthPx: scaledPx(720, slider),
    };
  }

  return {
    sizeRatio: slider,
    qrSizePx: Math.round(layout.frame.width * frameRatio * plate.codeBoost),
    platePaddingX: padding,
    platePaddingY: padding,
    qrToTextGap: 0,
    plateGap: scaledPx(plate.captionGap, slider),
    identityGap: 0,
    surfacePadding: scaledPx(plate.surfacePadding, slider),
    plateRadius: scaledPx(plate.radius, slider),
    textLayoutMaxWidth: scaledPx(plate.textMaxWidth, slider),
    fontCaption: scaledPx(plate.fonts.caption, slider),
    fontName: scaledPx(plate.fonts.name, slider),
    fontJob: scaledPx(plate.fonts.job, slider),
    plateMaxWidthPx: scaledPx(plate.maxWidth, slider),
  };
}

export function meetLogoInnerWidthPx(
  logoSizeRatio: number,
  layout: MeetLayoutConfig = MEET_LAYOUT,
): number {
  const slider = meetSlider(logoSizeRatio, "logo");
  return Math.round(layout.frame.width * slider * layout.logo.frameRatioAtFull);
}

export function enrichMeetBackgroundSettings(
  settings: Omit<
    MeetBackgroundSettings,
    "meetLayout" | "qrPlateMetrics" | "logoInnerWidthPx"
  > &
    Partial<
      Pick<
        MeetBackgroundSettings,
        "meetLayout" | "qrPlateMetrics" | "logoInnerWidthPx"
      >
    >,
): MeetBackgroundSettings {
  const sizeRatio = meetSlider(settings.sizeRatio, "qr");
  const logoSizeRatio = meetSlider(settings.logoSizeRatio, "logo");
  const layoutVariant = settings.layoutVariant ?? "corner";
  return {
    ...settings,
    sizeRatio,
    logoSizeRatio,
    meetLayout: MEET_LAYOUT,
    qrPlateMetrics: meetQrPlateMetrics(sizeRatio, layoutVariant),
    logoInnerWidthPx: meetLogoInnerWidthPx(logoSizeRatio),
  };
}

export function meetEditorFormValuesFromItem(
  item: MeetBackground,
): MeetBackgroundEditorFormValues {
  const settings = item.settings;
  return {
    name: item.name,
    digitalCardProfileId: item.digitalCardProfileId || null,
    qrCaption: settings.qrCaption ?? "",
    showEmployeeName: settings.showEmployeeName === true,
    showJobTitle: settings.showJobTitle === true,
    showLogo: settings.showLogo === true,
    logoUrl: settings.logoUrl ?? "",
    fontPreset: "default",
    layoutVariant: settings.layoutVariant ?? "corner",
    barWidth: settings.barWidth ?? "compact",
    corner: settings.corner ?? "bottom-right",
    logoCorner: settings.logoCorner ?? "top-left",
    opacity: settings.opacity ?? 0.92,
    qrBackgroundColor: settings.qrBackgroundColor ?? "#f5f5f5",
    marginPx: settings.marginPx ?? 24,
    sizeRatio: meetSlider(settings.sizeRatio, "qr"),
    logoOpacity: settings.logoOpacity ?? 0.92,
    logoBackgroundColor: settings.logoBackgroundColor ?? "#ffffff",
    logoMarginPx: settings.logoMarginPx ?? 24,
    logoSizeRatio: meetSlider(settings.logoSizeRatio, "logo"),
  };
}

export function meetSettingsFromFormValue(
  value: MeetBackgroundEditorSettings & {
    fontName?: string;
    fontFamily?: string;
    fontCssUrl?: string | null;
  },
): MeetBackgroundSettings {
  return enrichMeetBackgroundSettings({
    showQrCaption: !!value.qrCaption?.trim(),
    qrCaption: value.qrCaption || null,
    showEmployeeName: value.showEmployeeName ?? false,
    showJobTitle: value.showJobTitle ?? false,
    showLogo: value.showLogo ?? false,
    logoUrl: value.logoUrl || null,
    fontName: value.fontName?.trim() || "Default",
    fontFamily: value.fontFamily?.trim() || CANVAS_DEFAULT_FONT_FAMILY,
    fontCssUrl: value.fontCssUrl || null,
    layoutVariant: value.layoutVariant ?? "corner",
    barWidth: value.barWidth ?? "compact",
    corner: value.corner,
    logoCorner: value.logoCorner,
    opacity: value.opacity,
    qrBackgroundColor: value.qrBackgroundColor?.trim() || "#f5f5f5",
    marginPx: value.marginPx,
    sizeRatio: meetSlider(value.sizeRatio, "qr"),
    logoOpacity: value.logoOpacity,
    logoBackgroundColor: value.logoBackgroundColor?.trim() || "#ffffff",
    logoMarginPx: value.logoMarginPx,
    logoSizeRatio: meetSlider(value.logoSizeRatio, "logo"),
  });
}

export function buildMeetPreviewLayers(
  params: MeetBackgroundPreviewLayerParams,
): MeetPreviewLayers {
  const layoutVariant = params.layoutVariant ?? "corner";
  const barWidth = params.barWidth ?? "compact";
  const metrics = meetQrPlateMetrics(params.sizeRatio, layoutVariant);
  const caption =
    params.showQrCaption && params.qrCaption?.trim()
      ? params.qrCaption.trim()
      : null;
  const identityStyle: Record<string, string> = {};
  if (caption && !metrics.qrToTextGap) {
    identityStyle["marginTop"] = `${metrics.plateGap}px`;
  }
  const logoUrl = params.logoUrl?.trim() || null;
  const logoPlate = MEET_LAYOUT.logo.plate;
  const logoWidthPx = meetLogoInnerWidthPx(params.logoSizeRatio);
  const horizontal = layoutVariant === "bottom-bar";
  const fullOverlay = layoutVariant === "full-overlay";
  const qrBackground = meetPlateBackgroundColor(
    params.qrBackgroundColor,
    clampOpacity(params.opacity),
    "#f5f5f5",
  );
  const logoBackground = meetPlateBackgroundColor(
    params.logoBackgroundColor,
    clampOpacity(params.logoOpacity),
    "#ffffff",
  );

  let wrapStyle: Record<string, string>;
  if (fullOverlay) {
    wrapStyle = { inset: "0", left: "0", right: "0", top: "0", bottom: "0" };
  } else if (horizontal) {
    wrapStyle = meetBarPositionStyle(barWidth, params.marginPx);
  } else {
    wrapStyle = meetCornerPositionStyle(params.corner, params.marginPx);
  }

  const plateStyle: Record<string, string> = {
    backgroundColor: qrBackground,
    backdropFilter: "blur(12px)",
    WebkitBackdropFilter: "blur(12px)",
    padding: `${metrics.platePaddingY}px ${metrics.platePaddingX}px`,
    borderRadius: `${metrics.plateRadius}px`,
    boxSizing: "border-box",
  };

  if (fullOverlay) {
    plateStyle["width"] = "100%";
    plateStyle["height"] = "100%";
    plateStyle["display"] = "flex";
    plateStyle["flexDirection"] = "column";
    plateStyle["alignItems"] = "center";
    plateStyle["justifyContent"] = "center";
  } else if (horizontal) {
    plateStyle["display"] = "flex";
    plateStyle["flexDirection"] = "row";
    plateStyle["alignItems"] = "center";
    plateStyle["justifyContent"] = "center";
    plateStyle["gap"] = `${metrics.qrToTextGap}px`;
    if (barWidth === "full") {
      plateStyle["width"] = "100%";
    } else {
      plateStyle["minWidth"] =
        `${metrics.qrSizePx + metrics.platePaddingX * 2}px`;
      plateStyle["maxWidth"] = `${metrics.plateMaxWidthPx}px`;
      plateStyle["width"] = "auto";
    }
  } else {
    plateStyle["display"] = "flex";
    plateStyle["flexDirection"] = "column";
    plateStyle["alignItems"] = "center";
    plateStyle["minWidth"] =
      `${metrics.qrSizePx + metrics.platePaddingX * 2}px`;
    plateStyle["maxWidth"] = `${metrics.plateMaxWidthPx}px`;
    plateStyle["width"] = "max-content";
  }

  const surfaceStyle: Record<string, string> = horizontal
    ? {
        padding: `${metrics.surfacePadding}px`,
        flexShrink: "0",
        width: "auto",
        flex: "0 0 auto",
      }
    : { padding: `${metrics.surfacePadding}px`, flexShrink: "0" };

  const textColumnStyle: Record<string, string> = horizontal
    ? {
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        textAlign: "left",
        flex: "0 1 auto",
        minWidth: "0",
        maxWidth: `${metrics.textLayoutMaxWidth}px`,
        overflowWrap: "break-word",
        wordBreak: "break-word",
      }
    : {};

  if (horizontal && caption) {
    textColumnStyle["gap"] = `${metrics.plateGap}px`;
  }

  return {
    qr: {
      wrapStyle,
      plateStyle,
      surfaceStyle,
      textColumnStyle,
      identityStyle,
      qrSizePx: metrics.qrSizePx,
      fontCaption: metrics.fontCaption,
      fontName: metrics.fontName,
      fontJob: metrics.fontJob,
      horizontal,
      fullOverlay,
      plateGap: metrics.plateGap,
    },
    logo: {
      wrapStyle: meetCornerPositionStyle(
        fullOverlay
          ? params.logoCorner
          : meetLogoCornerAwayFromQr(params.corner, params.logoCorner),
        params.logoMarginPx,
      ),
      plateStyle: fullOverlay
        ? {
            width: `${logoWidthPx}px`,
          }
        : {
            backgroundColor: logoBackground,
            width: `${logoWidthPx}px`,
            padding: `${logoPlate.padding}px`,
            borderRadius: `${logoPlate.radius}px`,
          },
      bare: fullOverlay,
    },
    caption,
    logoUrl: params.showLogo ? logoUrl : null,
    renderedUrl: params.renderedImageUrl?.trim() || null,
  };
}

export function meetCornersExcept(
  excluded: MeetBackgroundCorner | null | undefined,
): MeetBackgroundCorner[] {
  if (!excluded) {
    return [...MEET_BACKGROUND_CORNERS];
  }
  return MEET_BACKGROUND_CORNERS.filter((corner) => corner !== excluded);
}

export function meetLogoCornerAwayFromQr(
  qrCorner: MeetBackgroundCorner,
  logoCorner: MeetBackgroundCorner,
): MeetBackgroundCorner {
  if (logoCorner !== qrCorner) {
    return logoCorner;
  }
  const opposite: Record<MeetBackgroundCorner, MeetBackgroundCorner> = {
    "bottom-right": "top-left",
    "bottom-left": "top-right",
    "top-right": "bottom-left",
    "top-left": "bottom-right",
  };
  return opposite[qrCorner];
}

export function meetFixOverlappingCorner(
  corner: MeetBackgroundCorner,
  logoCorner: MeetBackgroundCorner,
  changed: MeetCornerField,
): Partial<Pick<MeetBackgroundEditorFormValues, MeetCornerField>> {
  if (corner !== logoCorner) {
    return {};
  }
  if (changed === "corner") {
    return { logoCorner: meetLogoCornerAwayFromQr(corner, logoCorner) };
  }
  return { corner: meetLogoCornerAwayFromQr(logoCorner, corner) };
}

export function meetFallbackQrTarget(): string {
  return globalThis.location?.origin || "https://gsignature.com";
}

export function meetEmployeeRenderUrl(
  renders: MeetBackgroundEmployeeRender[] | undefined,
  email: string | null | undefined,
): string | null {
  const normalized = (email ?? "").trim().toLowerCase();
  if (!normalized || !renders?.length) {
    return null;
  }
  const row = renders.find(
    (render) => (render.email ?? "").trim().toLowerCase() === normalized,
  );
  const url = row?.meetBackground?.trim();
  return url && url.length > 0 ? url : null;
}

export function meetPreviewEmployeeName(
  employee: EmployeeWithBrand | null | undefined,
): string {
  if (!employee) {
    return "";
  }
  const full = employee.fullName?.trim();
  if (full) {
    return full;
  }
  const given = employee.givenName?.trim() ?? "";
  const family = employee.familyName?.trim() ?? "";
  return `${given} ${family}`.trim();
}

export function meetPreviewJobTitle(
  employee: EmployeeWithBrand | null | undefined,
): string {
  const jobTitle = employee?.dynamicVariables?.jobTitle;
  return typeof jobTitle === "string" ? jobTitle.trim() : "";
}

export function meetPreviewLogoUrl(
  showLogo: boolean | undefined,
  logoUrl: string | null | undefined,
  companyLogoUrl: string | null,
): string | null {
  if (!showLogo) {
    return null;
  }
  const selected = logoUrl?.trim();
  return selected || companyLogoUrl;
}

export function meetMediaImageLabel(url: string, images: Image[]): string {
  const found = images.find((image) => image.imageUrl === url);
  if (found?.name?.trim()) {
    return found.name.trim();
  }
  return url.length > 48 ? `${url.slice(0, 45)}…` : url;
}

export function meetCornerPositionStyle(
  corner: MeetBackgroundCorner,
  margin: number,
): Record<string, string> {
  const marginPx = `${margin}px`;
  switch (corner) {
    case "bottom-left":
      return { left: marginPx, bottom: marginPx, right: "auto", top: "auto" };
    case "top-right":
      return { right: marginPx, top: marginPx, left: "auto", bottom: "auto" };
    case "top-left":
      return { left: marginPx, top: marginPx, right: "auto", bottom: "auto" };
    default:
      return { right: marginPx, bottom: marginPx, left: "auto", top: "auto" };
  }
}

export function meetBarPositionStyle(
  barWidth: "compact" | "full",
  margin: number,
): Record<string, string> {
  const marginPx = `${margin}px`;
  if (barWidth === "full") {
    return {
      left: "0",
      right: "0",
      bottom: marginPx,
      top: "auto",
      width: "100%",
    };
  }
  return {
    left: "50%",
    bottom: marginPx,
    right: "auto",
    top: "auto",
    transform: "translateX(-50%)",
  };
}

function clampOpacity(value: number): number {
  return Math.min(1, Math.max(0, value));
}

function meetPlateBackgroundColor(
  hex: string | null | undefined,
  opacity: number,
  fallback = "#f5f5f5",
): string {
  let color = (hex ?? fallback).trim().toLowerCase();
  if (!color.startsWith("#")) {
    color = `#${color}`;
  }
  if (color.length === 4) {
    color = `#${color[1]}${color[1]}${color[2]}${color[2]}${color[3]}${color[3]}`;
  }
  if (!/^#[0-9a-f]{6}$/.test(color)) {
    color = fallback;
  }
  const r = Number.parseInt(color.slice(1, 3), 16);
  const g = Number.parseInt(color.slice(3, 5), 16);
  const b = Number.parseInt(color.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${clampOpacity(opacity)})`;
}
